/**
 * CircleShape Module
 * Makes a single circular element move based on mouse movement
 * 
 * Usage:
 * <div class="circle-shape" data-module="CircleShape" data-circle-move="x">Move on X axis</div>
 * <div class="circle-shape" data-module="CircleShape" data-circle-move="y">Move on Y axis</div>
 * <div class="circle-shape" data-module="CircleShape" data-circle-move="both">Move on both axes</div>
 * 
 * Optional attributes:
 * - data-circle-move: Movement direction ("x", "y", or "both")
 * - data-circle-intensity: Movement intensity (default: 0.1)
 * - data-circle-smooth: Smoothing factor (default: 0.1)
 */

import { module } from 'stereomodularjs';

export default class extends module {
    constructor(m) {
        super(m);
        
        this.mouse = { x: 0, y: 0 };
        this.targetMouse = { x: 0, y: 0 };
        this.isActive = false;
    }

    init() {
        this.circle = this.el;
        this.setupCircle();
        this.bindEvents();
        this.animate();
    }

    setupCircle() {
        // Get circle properties from data attributes
        this.moveType = this.circle.dataset.circleMove || 'both';
        this.intensity = parseFloat(this.circle.dataset.circleIntensity) || 0.1;
        this.smooth = parseFloat(this.circle.dataset.circleSmooth) || 0.1;

        // Get element's center position
        const rect = this.circle.getBoundingClientRect();
        this.centerX = rect.left + rect.width / 2;
        this.centerY = rect.top + rect.height / 2;

        // Initialize position values
        this.currentX = 0;
        this.currentY = 0;
        this.targetX = 0;
        this.targetY = 0;
    }

    bindEvents() {
        // Mouse move event
        document.addEventListener('mousemove', (e) => {
            this.targetMouse.x = e.clientX;
            this.targetMouse.y = e.clientY;
            
            if (!this.isActive) {
                this.isActive = true;
            }
        });

        // Mouse leave event to reset position
        document.addEventListener('mouseleave', () => {
            this.targetMouse.x = window.innerWidth / 2;
            this.targetMouse.y = window.innerHeight / 2;
        });

        // Window resize event
        window.addEventListener('resize', () => {
            this.setupCircle();
        });
    }

    updateMousePosition() {
        // Smooth mouse position interpolation
        this.mouse.x += (this.targetMouse.x - this.mouse.x) * 0.1;
        this.mouse.y += (this.targetMouse.y - this.mouse.y) * 0.1;
    }

    updateCircle() {
        // Calculate distance from mouse to circle center
        const deltaX = this.mouse.x - this.centerX;
        const deltaY = this.mouse.y - this.centerY;

        // Calculate target position based on move type
        switch (this.moveType.toLowerCase()) {
            case 'x':
                this.targetX = deltaX * this.intensity;
                this.targetY = 0;
                break;
            case 'y':
                this.targetX = 0;
                this.targetY = deltaY * this.intensity;
                break;
            case 'both':
            default:
                this.targetX = deltaX * this.intensity;
                this.targetY = deltaY * this.intensity;
                break;
        }

        // Smooth interpolation to target position
        this.currentX += (this.targetX - this.currentX) * this.smooth;
        this.currentY += (this.targetY - this.currentY) * this.smooth;

        // Apply transform
        this.circle.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
    }

    animate() {
        this.updateMousePosition();
        this.updateCircle();
        requestAnimationFrame(() => this.animate());
    }

    // Public method to refresh circle (useful for dynamic content)
    refresh() {
        this.setupCircle();
    }

    // Public method to destroy the module
    destroy() {
        this.circle.style.transform = '';
    }
}
